import { createFileRoute } from "@tanstack/react-router";
import { pageHead } from "@/lib/seo";
import { Topbar } from "@/components/calv/Topbar";
import { Footer } from "@/components/calv/Footer";
import { LogoReferences } from "@/components/calv/LogoReferences";
import { Hero } from "@/components/calv/Hero";
import { Section } from "@/components/calv/Section";
import { ProcessFlow } from "@/components/calv/ProcessFlow";
import { Capabilities } from "@/components/calv/Capabilities";
import { StackGrid } from "@/components/calv/StackGrid";
import { CTABlock } from "@/components/calv/CTABlock";
import { RevealOnScroll } from "@/components/calv/RevealOnScroll";
import { RelatedServices } from "@/components/calv/RelatedServices";
import { relatedBySource } from "@/lib/related-services-data";

export const Route = createFileRoute("/leistungen/onlineshop-entwicklung")({
  head: () => {
    const h = pageHead("shopEntwicklung", "de");
    return { meta: h.meta, links: h.links };
  },
  component: ShopPage,
});

const steps = [
  { num: "01", title: "Geschäftsmodell & Datenmodell", body: "Wir klären Sortiment, Preislogik, Zielgruppen und Verkaufskanäle — und wie Produktdaten strukturiert werden müssen, damit sie überall sauber laufen." },
  { num: "02", title: "Shop & Checkout", body: "Aufbau des Shop-Systems mit Fokus auf einen reibungslosen, conversion-starken Checkout, performante Kategorie- und Produktseiten und eine wartbare technische Basis." },
  { num: "03", title: "Schnittstellen & Integration", body: "Anbindung von Payment, Versand, Warenwirtschaft und ERP sowie E-Commerce-Tracking, Produktfeeds und Marketing-Anforderungen — strukturell, nicht nachträglich." },
  { num: "04", title: "Skalierung & Pflege", body: "Nach dem Go-live begleiten wir bei Sortimentserweiterung, Performance, neuen Kanälen und laufender technischer Pflege." },
];

const capabilities = [
  { name: "Checkout-Optimierung", detail: "kurz, klar, conversion-stark" },
  { name: "Payment-Integration", detail: "Zahlarten, Steuern, Rechnungslogik" },
  { name: "Produktdaten & Feeds", detail: "strukturiert, exportierbar, marketingfähig" },
  { name: "ERP- & Versand-Schnittstellen", detail: "automatisiert statt manuell" },
  { name: "E-Commerce-Tracking", detail: "Purchase-Events, Remarketing" },
  { name: "Performance-Optimierung", detail: "Ladezeiten auch bei großem Sortiment" },
  { name: "Internationalisierung", detail: "Sprachen, Währungen, Steuerregeln" },
  { name: "Skalierung & Pflege", detail: "mit dem Sortiment mitwachsend" },
];

const stack = [
  { name: "Shopware", small: "E-Commerce, DACH" },
  { name: "Shopify", small: "E-Commerce, schnell live", href: "/leistungen/onlineshop-entwicklung/shopify-agentur", ariaLabel: "Mehr zur Shopify-Agentur" },
  { name: "WooCommerce", small: "flexibel, WordPress-Basis" },
];

const mechanics = [
  { title: "Checkout & Conversion", body: "Jeder unnötige Schritt kostet Umsatz. Wir bauen Checkouts kurz, klar und vertrauenswürdig." },
  { title: "Payment & Steuern", body: "Zahlarten, Steuerregeln, Rechnungslogik und länderspezifische Anforderungen sauber integriert." },
  { title: "Produktdaten & PIM", body: "Produktdaten leben in einem System, nicht in Tabellen — strukturiert, exportierbar, für Onlineshop und Marketing gleichermaßen nutzbar.", link: { label: "Verknüpfung mit Delta →", href: "/leistungen/delta" } },
  { title: "Schnittstellen", body: "ERP, Warenwirtschaft, Versanddienstleister und Buchhaltung greifen automatisch ineinander, statt manuell nachgepflegt zu werden." },
  { title: "E-Commerce-Tracking", body: "Purchase-Events, Produktfeeds und dynamisches Remarketing von Anfang an strukturell verankert — die Basis für profitables Performance-Marketing." },
  { title: "Rechtssicherheit", body: "Widerrufsbelehrung, Preisangabenverordnung und Pflichtangaben korrekt umgesetzt." },
];

function ShopPage() {
  return (
    <>
      <Topbar />
      <main id="main">
        <Hero
          variant="short"
          number="01"
          eyebrow="Onlineshop-Entwicklung"
          headline={<>Ein Onlineshop ist <em>keine Website mit Bezahlbutton.</em> Er ist ein verkaufendes System.</>}
          lead="Wir entwickeln Onlineshops auf Shopware, Shopify und WooCommerce, die nicht nur Produkte zeigen, sondern verkaufen — mit sauberem Checkout, belastbaren Schnittstellen und gepflegten Produktdaten als Fundament. Geeignet für Händler, die ihren Shop als Umsatzmotor begreifen, nicht als digitalen Katalog."
          metaStrip={[
            "Shopware · Shopify · WooCommerce",
            "PIM & Feed-Management",
            "ERP- & Versand-Anbindung",
            "E-Commerce-Tracking als Standard",
          ]}
          primaryCta={{
            eyebrow: "Kostenfrei · circa 30 Minuten · keine Verpflichtung",
            label: "Strategiegespräch anfragen",
            to: "/kontakt",
          }}
          secondaryCta={{
            label: "Oder: Onlineshop-Checkliste ohne Anmeldung herunterladen",
            to: "/kontakt",
          }}
          breadcrumbs={[{ label: "Start", to: "/" }, { label: "Leistungen", to: "/leistungen" }, { label: "Onlineshop-Entwicklung" }]}
        />


        <Section number="02" kicker="Standpunkt" headline={<>Ein Onlineshop scheitert selten am Design. Er scheitert <em>an der Mechanik.</em></>}>
          <RevealOnScroll>
            <div className="mb-10 rounded-lg border-l-4 border-lime bg-bg-subtle p-5 text-[15px] leading-relaxed text-ink-2">
              Sie brauchen eine Website ohne Verkaufsfunktion? Das ist eine eigene Disziplin —{" "}
              <a href="/leistungen/website-entwicklung" className="font-medium text-ink underline decoration-lime decoration-2 underline-offset-4 hover:decoration-ink">
                zur Website-Entwicklung →
              </a>
            </div>
            <div className="prose max-w-2xl text-[17px] leading-relaxed text-ink-2">
              <p className="mb-5">E-Commerce hat eine Komplexität, die eine klassische Website nicht kennt. Sobald Geld, Bestand und Logistik im Spiel sind, entscheidet nicht das Aussehen über den Erfolg, sondern die Mechanik dahinter: Wie sauber ist der Checkout? Wie zuverlässig fließen Bestände, Bestellungen und Preise zwischen Onlineshop, Warenwirtschaft und Buchhaltung? Wie gut sind Produktdaten gepflegt und für Marketing nutzbar?</p>
              <p className="mb-5">In der Praxis sehen wir immer wieder dieselben Bremsen: ein Checkout mit zu vielen Schritten und Abbrüchen, Produktdaten, die in Tabellen statt in einem System leben, Schnittstellen zu ERP und Versand, die manuell nachgepflegt werden müssen, fehlendes oder lückenhaftes E-Commerce-Tracking, und ein Onlineshop, der mit dem Sortiment nicht mitwächst. Das Ergebnis: Umsatz wird auf der Strecke liegen gelassen, der nie sichtbar wird.</p>
              <p>Wir entwickeln Onlineshops als verkaufende Systeme. Am Anfang steht nicht die Produktseite, sondern das Geschäftsmodell: Wie wird verkauft, an wen, über welche Kanäle, mit welcher Logistik und welchen Systemen im Hintergrund. Erst danach bauen wir.</p>
            </div>
          </RevealOnScroll>
        </Section>

        <Section variant="tinted" number="03" kicker="Vorgehen" headline={<>Vier Phasen vom <em>Geschäftsmodell</em> zum laufenden Onlineshop.</>}>
          <ProcessFlow steps={steps} />
        </Section>

        <Section number="04" kicker="Mechanik" headline={<>Was einen Onlineshop <em>zum System</em> macht.</>} caption="Die Themen, die über Erfolg entscheiden — und die eine Website nicht hat.">
          <RevealOnScroll>
            <div className="grid gap-6 md:grid-cols-2">
              {mechanics.map((m) => (
                <div key={m.title} className="rounded-lg border border-rule bg-white p-7">
                  <h3 className="mb-3 font-display text-xl text-ink">{m.title}</h3>
                  <p className="text-[15px] leading-relaxed text-ink-2">{m.body}</p>
                  {m.link && (
                    <a href={m.link.href} className="mt-4 inline-flex items-center font-mono text-xs uppercase tracking-wider text-ink-2 hover:text-lime-deep">
                      {m.link.label}
                    </a>
                  )}
                </div>
              ))}
            </div>
          </RevealOnScroll>
        </Section>

        <Section number="05" kicker="Capabilities & Stack" headline={<>Womit wir Onlineshops <em>verkaufsfähig</em> machen.</>}>
          <Capabilities items={capabilities} />
          <div className="mt-12">
            <p className="eyebrow mb-5">Stack</p>
            <StackGrid items={stack} />
          </div>
        </Section>

        <LogoReferences locale="de" number="06" />

        <RelatedServices
          locale="de"
          fromId="shop-entwicklung"
          number="07"
          items={relatedBySource["shop-entwicklung"]}
        />

        <Section number="08" kicker="Engagement">
          <CTABlock
            headline={<>Lassen Sie uns über Ihren <em>Onlineshop</em> sprechen.</>}
            body="Ob neuer Onlineshop, Replatforming oder ein bestehender Shop, der mehr leisten soll — wir klären im Strategiegespräch, was zu Ihrem Sortiment, Ihrer Logistik und Ihrem Geschäftsmodell passt. Konkret, ehrlich und ohne Verkaufsdruck."
            primary={{ label: "Strategiegespräch anfragen", to: "/kontakt" }}
            secondary={{ label: "Alle Leistungen", to: "/leistungen" }}
          />
        </Section>
      </main>
      <Footer />
    </>
  );
}
